import {
  Close as CloseIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoLoader from "../common/LogoLoader";
import { useNotification } from "../../contexts/NotificationContext";
import { useActiveRuns } from "../../hooks/queries/useActiveRuns";
import { queryKeys } from "../../hooks/queries/keys";
import {
  ActiveDiscoveryRun,
  ActiveEvalTask,
  researchApi,
} from "../../services/researchApi";

interface ActiveRunsModalProps {
  open: boolean;
  onClose: () => void;
}

function StatusChip({ status }: { status: string }) {
  if (status === "running") {
    return (
      <Chip
        label="Running"
        size="small"
        color="warning"
        icon={<CircularProgress size={12} color="inherit" />}
      />
    );
  }
  return <Chip label="Queued" size="small" color="info" />;
}

function formatWhen(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

const ActiveRunsModal: React.FC<ActiveRunsModalProps> = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);
  const [cancelling, setCancelling] = useState<Set<string>>(new Set());
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // Jump to the track's discovery (longList) / evaluation (shortList) view.
  const goToTrack = (
    projectId: string,
    trackId: string,
    stage: "longList" | "shortList",
  ) => {
    navigate(`/research/${projectId}?track=${trackId}&stage=${stage}`);
    onClose();
  };
  const queryClient = useQueryClient();

  const { data, isLoading } = useActiveRuns(open);
  const discovery = data?.discovery ?? [];
  const evaluation = data?.evaluation ?? [];

  const runCancel = async (key: string, action: () => Promise<void>) => {
    setCancelling((prev) => new Set(prev).add(key));
    try {
      await action();
      await queryClient.invalidateQueries({
        queryKey: queryKeys.research.activeRuns,
      });
      showNotification("Cancellation requested.", "success");
    } catch {
      showNotification("Failed to cancel. Please try again.", "error");
    } finally {
      setCancelling((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const cancelButton = (key: string, action: () => Promise<void>) => (
    <Tooltip title="Cancel" arrow>
      <span>
        <IconButton
          size="small"
          color="error"
          disabled={cancelling.has(key)}
          onClick={() => runCancel(key, action)}
        >
          {cancelling.has(key) ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <StopIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Active runs
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 420 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Evaluation (${evaluation.length})`} />
          <Tab label={`Discovery (${discovery.length})`} />
        </Tabs>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <LogoLoader size="section" surface="paper" label="Loading the runs" />
          </Box>
        )}

        {!isLoading && tab === 0 && (
          <EvaluationTable
            rows={evaluation}
            cancelButton={cancelButton}
            onOpenTrack={goToTrack}
          />
        )}
        {!isLoading && tab === 1 && (
          <DiscoveryTable
            rows={discovery}
            cancelButton={cancelButton}
            onOpenTrack={goToTrack}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

function EvaluationTable({
  rows,
  cancelButton,
  onOpenTrack,
}: {
  rows: ActiveEvalTask[];
  cancelButton: (key: string, action: () => Promise<void>) => React.ReactNode;
  onOpenTrack: (
    projectId: string,
    trackId: string,
    stage: "longList" | "shortList",
  ) => void;
}) {
  if (rows.length === 0) {
    return <EmptyState label="No evaluations running or queued." />;
  }
  return (
    <TableContainer sx={{ maxHeight: 480, overflowX: "auto" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Candidate</TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Track</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Scheduled by</TableCell>
            <TableCell>Enqueued</TableCell>
            <TableCell align="right">Cancel</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.task_id}
              hover
              onClick={() =>
                onOpenTrack(r.project_id, r.track_id, "shortList")
              }
              sx={{ cursor: "pointer" }}
            >
              <TableCell>{r.candidate_name}</TableCell>
              <TableCell>{r.project_name}</TableCell>
              <TableCell>{r.track_name}</TableCell>
              <TableCell>
                <StatusChip status={r.status} />
              </TableCell>
              <TableCell>{r.scheduled_by ?? "—"}</TableCell>
              <TableCell>{formatWhen(r.enqueued_at)}</TableCell>
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                {r.track_entity_id
                  ? cancelButton(`task:${r.task_id}`, () =>
                      researchApi.cancelEvaluation(r.track_entity_id as string),
                    )
                  : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DiscoveryTable({
  rows,
  cancelButton,
  onOpenTrack,
}: {
  rows: ActiveDiscoveryRun[];
  cancelButton: (key: string, action: () => Promise<void>) => React.ReactNode;
  onOpenTrack: (
    projectId: string,
    trackId: string,
    stage: "longList" | "shortList",
  ) => void;
}) {
  if (rows.length === 0) {
    return <EmptyState label="No discovery runs running or queued." />;
  }
  return (
    <TableContainer sx={{ maxHeight: 480, overflowX: "auto" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Project</TableCell>
            <TableCell>Track</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Scheduled by</TableCell>
            <TableCell>Enqueued</TableCell>
            <TableCell align="right">Cancel</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.run_id}
              hover
              onClick={() => onOpenTrack(r.project_id, r.track_id, "longList")}
              sx={{ cursor: "pointer" }}
            >
              <TableCell>{r.project_name}</TableCell>
              <TableCell>{r.track_name}</TableCell>
              <TableCell>
                <StatusChip status={r.status} />
              </TableCell>
              <TableCell>{r.scheduled_by ?? "—"}</TableCell>
              <TableCell>{formatWhen(r.enqueued_at)}</TableCell>
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                {cancelButton(`run:${r.run_id}`, () =>
                  researchApi.cancelRun(r.run_id),
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default ActiveRunsModal;
